import { useMemo, useState } from "react"
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { SortableItem } from "lib/@components/SortableItem"
import { DragHandle } from "lib/dragHandle"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { RuleSetReferenceOption } from "./rule-set-reference-option"
import { RuleSetReferenceSummary } from "./rule-set-reference-summary"
import { buildRuleSetSummary } from "./rule-set-summary-utils"

export function RuleSetReferenceCard({ item, onUpdate, onRemove, readOnly }) {
    const { availableRuleSets = [], conditionFields = [], currentRuleSetId, rules = [] } = useBoundContext()
    const [dragProps, setDragProps] = useState({})
    const [editing, setEditing] = useState(shouldStartEditing)
    const [draft, setDraft] = useState(initialDraft)

    const options = useMemo(buildOptions, [availableRuleSets, conditionFields, currentRuleSetId, rules, item.id])
    const selectedValue = useMemo(resolveSelectedValue, [options, draft.ruleSetId, item.ruleSetName])
    const label = selectedValue?.label || item.ruleSetName || item.ruleSetId || "No rule set selected"
    const enabled = item.enabled !== false
    const opacity = getOpacity()

    return (
        <SortableItem id={item.id} setDragProps={setDragProps} disabled={readOnly}>
            <If value={editing}>
                <Card variant="outlined" sx={{ mb: 1 }} data-cy="rule-set-reference-editing">
                    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                        <Stack spacing={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.primary"
                                fontWeight={700}
                                sx={{ display: "block", fontSize: 16 }}
                            >
                                Rule Set
                            </Typography>
                            <Autocomplete
                                data-cy="rule-set-reference-picker"
                                options={options}
                                getOptionLabel={getOptionLabel}
                                isOptionEqualToValue={isOptionEqual}
                                value={selectedValue}
                                onChange={handleRuleSetChange}
                                renderInput={renderRuleSetInput}
                                renderOption={RuleSetReferenceOption}
                                fullWidth
                            />
                            <If value={selectedValue?.summary}>
                                <RuleSetReferenceSummary summary={selectedValue?.summary} />
                            </If>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 1 }}>
                                <Button onClick={handleSave} variant="contained" disabled={!draft.ruleSetId}>
                                    Save Rule Set
                                </Button>
                                <Button onClick={handleCancel} variant="text">
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
                <Else>
                    <Card variant="outlined" sx={{ mb: 1 }} data-cy="rule-set-reference-summary">
                        <CardContent
                            sx={{
                                py: 2.5,
                                "&:last-child": { pb: 2.5 },
                                opacity,
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <If value={!readOnly}>
                                        <DragHandle {...dragProps} />
                                    </If>
                                    <Typography variant="body1" sx={{ flex: 1 }}>
                                        <Box component="span" color="text.secondary">
                                            Apply rule set{" "}
                                        </Box>
                                        <Box component="span" fontWeight={600}>
                                            {label}
                                        </Box>
                                    </Typography>
                                    <If value={!readOnly}>
                                        <Switch
                                            checked={enabled}
                                            onChange={handleToggleEnabled}
                                            data-cy="rule-set-reference-enabled-toggle"
                                        />
                                        <IconButton
                                            aria-label="Edit reference"
                                            onClick={handleEdit}
                                            data-cy="rule-set-reference-edit"
                                            sx={{ p: 0.5 }}
                                        >
                                            <Iconify icon="solar:pen-bold" width={20} />
                                        </IconButton>
                                        <IconButton
                                            aria-label="Remove reference"
                                            onClick={handleRemove}
                                            data-cy="rule-set-reference-remove"
                                            sx={{ p: 0.5 }}
                                        >
                                            <Iconify icon="mdi:delete-outline" width={20} />
                                        </IconButton>
                                    </If>
                                </Stack>
                                <Box sx={{ pl: 4 }}>
                                    <RuleSetReferenceSummary summary={selectedValue?.summary} />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Else>
            </If>
        </SortableItem>
    )

    function buildOptions() {
        // FIQ-1634: exclude rule sets already referenced by other cards so the
        // same rule set can't be added twice (this card's own selection stays pickable)
        const alreadyReferenced = new Set(
            rules
                .filter((rule) => rule.type === "ruleSetReference" && rule.id !== item.id && rule.ruleSetId)
                .map((rule) => rule.ruleSetId)
        )
        return (availableRuleSets || [])
            .filter((ruleSet) => ruleSet.id !== currentRuleSetId && !alreadyReferenced.has(ruleSet.id))
            .map((ruleSet) => ({
                id: ruleSet.id,
                label: ruleSet.name,
                ruleSet,
                summary: buildRuleSetSummary(ruleSet, conditionFields),
            }))
    }

    function resolveSelectedValue() {
        if (!draft.ruleSetId) return null
        const match = options.find(matchesDraftRuleSet)
        if (match) return match
        if (item.ruleSetName) return { id: draft.ruleSetId, label: item.ruleSetName }
        return { id: draft.ruleSetId, label: draft.ruleSetId }
    }

    function matchesDraftRuleSet(option) {
        return option.id === draft.ruleSetId
    }

    function getOptionLabel(option) {
        return option?.label || ""
    }

    function isOptionEqual(option, value) {
        return option?.id === value?.id
    }

    function handleRuleSetChange(_, value) {
        setDraft((prev) => ({
            ...prev,
            ruleSetId: value?.id || null,
            ruleSetName: value?.label || null,
        }))
    }

    function renderRuleSetInput(params) {
        return <TextField {...params} label="Rule Set" placeholder="Select a rule set..." />
    }

    function handleSave() {
        if (readOnly) return
        onUpdate(item.id, {
            type: "ruleSetReference",
            enabled: draft.enabled !== false,
            ruleSetId: draft.ruleSetId,
            ruleSetName: draft.ruleSetName,
        })
        setEditing(false)
    }

    function handleCancel() {
        if (readOnly) return
        if (!item.ruleSetId) {
            onRemove(item.id)
            return
        }
        setDraft({ ...item })
        setEditing(false)
    }

    function handleEdit() {
        if (readOnly) return
        setDraft({ ...item })
        setEditing(true)
    }

    function handleToggleEnabled(e) {
        if (readOnly) return
        onUpdate(item.id, { enabled: e.target.checked })
    }

    function handleRemove() {
        if (readOnly) return
        onRemove(item.id)
    }

    function getOpacity() {
        if (enabled) return 1
        return 0.5
    }

    function shouldStartEditing() {
        if (readOnly) return false
        return !item.ruleSetId
    }

    function initialDraft() {
        return { ...item }
    }
}
